class MContactForm extends HTMLElement {
  constructor() {
    super();

    this.customPlaceholderSelector = 'm-js-placeholder';
    this.customHeadingSelector = 'm-js-heading';
    this.customHiddingSelector = 'm-js-hidding';
    this.customAppearingSelector = 'm-js-appearing';
    this.customPreselectSelector = 'm-js-preselect';
    this.customExcelFormatsSelector = 'm-js-excel-formats';
    this.customShareValuesSelector = 'm-js-share-form-values';
    this.customSortingSelector = 'm-js-sort-options';
    this.customInjectHTML = 'm-js-inject';
    this.inputsSelector = 'input, select, textarea';
    this.fileFormats = 'jpg, jpeg, png, pdf, zip';
    this.fileFormatsExcel = 'xls, xlsx, csv';
  }

  static async sleep(time) {
    await new Promise((resolve) => setTimeout(resolve, time));
  }

  async connectedCallback() {
    await this.constructor.sleep(10);
    this.prepare();

    if (!this.form) {
      await this.connectedCallback();
    } else {
      this.init();
      this.classList.add('m-js-mounted');
    }
  }

  prepare() {
    this.form = this.querySelector('form.bcontact-form');
    this.customPlaceholders = this.querySelectorAll('.' + this.customPlaceholderSelector);
    this.customHeadings = this.querySelectorAll('.' + this.customHeadingSelector);
    this.customHiddings = this.querySelectorAll('.' + this.customHiddingSelector);
    this.customAppearings = this.querySelectorAll('.' + this.customAppearingSelector);
    this.customPreselects = this.querySelectorAll('.' + this.customPreselectSelector);
    this.customInjects = this.querySelectorAll('.' + this.customInjectHTML);
    this.allInputs = this.querySelectorAll(this.inputsSelector);
    this.fileFields = this.querySelectorAll('.bcontact-file-wrapper');
    this.submitBtn = this.querySelector('.bcontact-submit-btn');
    this.dropdownsToSort = this.querySelectorAll('.' + this.customSortingSelector + ' select');
  }

  init() {
    this.removeEmptyTitles();
    this.setSubmitButtonClasses();
    this.setPlaceholders();
    this.injectCustomHTML();
    this.setHeadings();
    this.setHiddings();
    this.setAppearings();
    this.setFileUploads();
    this.preselectFields();
    this.sortDropdownsOptions();

    if (this.classList.contains(this.customShareValuesSelector)) {
      this.initShareValues();
    }
  }

  initShareValues() {
    const allFormsHolder = this.closest('.m-forms-to-preserve');

    if (allFormsHolder) {
      const allFormsExceptCurrent = Array.from(allFormsHolder.querySelectorAll('m-contact-form')).filter(
        (form) => form !== this,
      );

      this.form.querySelectorAll('input,textarea').forEach((input) => {
        input.addEventListener('keyup', () => {
          allFormsExceptCurrent.forEach((form) => {
            const field = form.querySelector('[name="' + input.name + '"]');
            if (field) {
              field.value = input.value;
              field.dispatchEvent(new Event('input'));
            }
          });
        });
      });
    }
  }

  sortDropdownsOptions() {
    if (this.dropdownsToSort.length) {
      this.dropdownsToSort.forEach((dropdown) => {
        const options = Array.from(dropdown.options);
        const disabledOptions = options.filter((option) => option.disabled);
        const enabledOptions = options.filter((option) => !option.disabled);

        disabledOptions.sort((a, b) => a.text.localeCompare(b.text));
        enabledOptions.sort((a, b) => a.text.localeCompare(b.text));

        const sortedOptions = [...disabledOptions, ...enabledOptions];

        dropdown.innerHTML = '';
        sortedOptions.forEach((option) => dropdown.add(option));
      });
    }
  }

  clearOtherFormsSharedValues() {
    if (this.classList.contains(this.customShareValuesSelector)) {
      const allFormsHolder = this.closest('.m-forms-to-preserve');

      if (allFormsHolder) {
        const allFormsExceptCurrent = Array.from(allFormsHolder.querySelectorAll('m-contact-form')).filter(
          (form) => form !== this,
        );

        allFormsExceptCurrent.forEach((form) => {
          form
            .querySelectorAll(
              '.bcontact-field:not(.m-js-preselect) input,.bcontact-field:not(.m-js-preselect) textarea',
            )
            .forEach((input) => {
              input.value = '';
            });
        });
      }
    }
  }

  removeEmptyTitles() {
    const title = this.querySelector('.bcontact-title');
    const description = this.querySelector('.bcontact-description');

    title.dataset.mHidden = 'afterFormSubmitted';
    description.dataset.mHidden = 'afterFormSubmitted';

    if (title && title.textContent.trim().length == 1) title.remove();
    if (description && description.textContent.trim().length == 1) description.remove();
  }

  setSubmitButtonClasses() {
    this.submitBtn.classList.add('btn', 'btn--primary');
    this.submitBtnText = this.submitBtn.textContent;
  }

  setSubmitButtonLoading() {
    this.submitBtn.textContent = this.submitBtnText;
    this.submitBtn.classList.add('is-loading');

    setTimeout(() => {
      this.submitBtn.classList.add('is-success');
    }, 200);
  }

  setHeadings() {
    this.customHeadings.forEach((element) => {
      const content = document.createElement('div');
      content.classList.add('m-bcontact-big-heading');
      content.classList.add('h4');
      const targetValue = (/m-js-heading-value-([\w-]+)/.exec(element.className) || ['', ''])[1];
      const readableTargetValue = targetValue.replace(/-/g, ' ');
      content.innerHTML = readableTargetValue;
      element.parentNode.insertBefore(content, element);
    });
  }

  injectCustomHTML() {
    this.customInjects.forEach((element) => {
      const targetID = (/m-js-inject-target-([\w-]+)/.exec(element.className) || ['', ''])[1];
      const targetElement = this.querySelector('.' + targetID);
      if (targetElement) {
        const clone = targetElement.cloneNode(true);
        targetElement.remove();
        if (targetID.includes('header')) {
          element.parentNode.insertBefore(clone, element);
        } else {
          element.parentNode.insertBefore(clone, element.nextSibling);
        }
      }
    });
  }

  setPlaceholders() {
    this.customPlaceholders.forEach((element) => {
      const targetValue = (/m-js-placeholder-value-([\w-.]+)/.exec(element.className) || ['', ''])[1];
      const readableTargetValue = targetValue.replace(/-/g, ' ');
      element.querySelector(this.inputsSelector).placeholder = readableTargetValue;
    });
  }

  setHiddings() {
    this.customHiddings.forEach((element) => {
      const select = element.querySelector('select');
      const targetValue = (/m-js-hidding-value-([\w-]+)/.exec(element.className) || ['', ''])[1];
      const readableTargetValue = targetValue.replace(/-/g, ' ');

      select.addEventListener('change', (e) => {
        const targetElement = this.querySelector(
          '.bcontact-field__' + (/m-js-hidding-target-([\w-]+)/.exec(element.className) || ['', ''])[1],
        );
        if (targetValue && targetElement && e.target.value == readableTargetValue) {
          targetElement.style.display = 'none';
        } else {
          targetElement.style.display = 'block';
        }
      });
    });
  }

  setAppearings() {
    this.customAppearings.forEach((element) => {
      const select = element.querySelector('select');
      const targetValue = (/m-js-appearing-value-([\w-]+)/.exec(element.className) || ['', ''])[1];
      const targetElement = this.querySelector(
        '.bcontact-field__' + (/m-js-appearing-target-([\w-]+)/.exec(element.className) || ['', ''])[1],
      );
      const readableTargetValue = targetValue.replace(/-/g, ' ');

      if (targetElement) targetElement.style.display = 'none';

      select.addEventListener('change', (e) => {
        if (targetValue && targetElement && e.target.value == readableTargetValue) {
          targetElement.style.display = 'block';
        } else {
          targetElement.style.display = 'none';
        }
      });
    });
  }

  setFileUploads() {
    this.fileFields.forEach((file) => {
      this.setFileAcceptTypes(file);
      this.setFileContent(file);
      this.setFileListener(file);
    });

    this.allInputs.forEach((input) => {
      this.setFileContentFallback(input);
    });
  }

  setFileAcceptTypes() {
    setTimeout(() => {
      this.querySelectorAll('.bcontact-file-wrapper').forEach((file) => {
        const fileContainer = file.closest('.bcontact-field');
        const accept = fileContainer.classList.contains(this.customExcelFormatsSelector)
          ? '.' + this.fileFormatsExcel.replaceAll(/\s/g, '').split(',').join(', .')
          : '.' + this.fileFormats.replaceAll(/\s/g, '').split(',').join(', .');
        file.querySelector('input[type="file"]').setAttribute('accept', accept);
      });
    }, 200);
  }

  setFileContent(file) {
    if (!file.querySelector('.m-bcontact-file-content')) {
      const label = file.parentNode.querySelector('label');
      label.setAttribute('for', '');

      const fileContainer = file.closest('.bcontact-field');
      const accept = fileContainer.classList.contains(this.customExcelFormatsSelector)
        ? this.fileFormatsExcel
        : this.fileFormats;
      const content = document.createElement('div');
      content.classList.add('m-bcontact-file-content');
      content.innerHTML += '<span>' + window.theme.strings.uploadField.clickText + '</span>';
      content.innerHTML += '<p>' + window.theme.strings.uploadField.fileTypes + ': ' + accept + '.</p>';
      content.innerHTML += '<p>' + window.theme.strings.uploadField.maximumFileSize + '</p>';
      file.appendChild(content);
    }
  }

  setFileListener(file) {
    file.addEventListener('click', (e) => {
      if (e.target.closest('.bcontact-file-box__close-btn')) return;
      const input = file.querySelector('input');
      if (input) input.click();
    });
  }

  setFileContentFallback(input) {
    input.addEventListener('change', () => {
      setTimeout(() => {
        this.fileFields = this.querySelectorAll('.bcontact-file-wrapper');
        this.fileFields.forEach((file) => {
          if (!file.querySelector('.m-bcontact-file-content')) {
            this.setFileContent(file);
            this.setFileListener(file);
            this.setFileAcceptTypes(file);
          }
        });
      }, 100);
    });
  }

  async updateSuccessMessage() {
    await this.constructor.sleep(10);

    if (!this.querySelector('.bcontact-success-message')) {
      await this.updateSuccessMessage();
    } else {
      const message = this.querySelector('.bcontact-success-message');
      const notification = this.querySelector('.bcontact-success-notification');
      const button = document.createElement('a');

      message.classList.add('h5');
      button.classList.add('btn', 'btn--secondary', 'btn--lg');
      button.href = window.theme.routes.root;
      button.innerHTML = window.theme.strings.goToHome;
      notification.appendChild(button);

      this.querySelectorAll('[data-m-hidden="afterFormSubmitted"]').forEach((element) => element.remove());
      setTimeout(() => this.scrollIntoView({ behavior: 'smooth', block: 'start' }), 10);
    }
  }

  preselectFields() {
    this.customPreselects.forEach((element) => {
      const targetValue = (/m-js-preselect-([\w-]+)/.exec(element.className) || ['', ''])[1];
      const readableTargetValue = targetValue.replace(/(-[a-z])/gi, ($1) => $1.toUpperCase().replace('-', ''));
      const input = element.querySelector(this.inputsSelector);
      if (input.nodeName == 'SELECT') {
        const options = input.options;
        for (let i = 0; i < options.length; i++) {
          if (options[i].value === this.dataset[readableTargetValue]) {
            input.value = this.dataset[readableTargetValue];
            input.dispatchEvent(new Event('change'));
            break;
          }
        }
      } else {
        input.value = this.dataset[readableTargetValue];
        input.dispatchEvent(new Event('input'));
      }
    });

    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);

    this.allInputs.forEach((input) => {
      for (const key of urlParams.keys()) {
        if (input.name == `contact[${key}]`) {
          input.value = urlParams.get(key);
          input.dispatchEvent(new Event(input.nodeName == 'SELECT' ? 'change' : 'input'));
        }
      }
    });
  }
}

customElements.define('m-contact-form', MContactForm);

document.addEventListener('bcontact:fileRemoved', (evt) => {
  if (evt.detail) {
    document
      .querySelector('.bcontact-field__' + evt.detail)
      .closest('m-contact-form')
      .setFileAcceptTypes();
  }
});

document.addEventListener('bcontact:beforeFormSubmitted', (evt) => {
  if (evt.detail) {
    document
      .querySelector('.bcontact-' + evt.detail)
      .closest('m-contact-form')
      .setSubmitButtonLoading();
  }
});

document.addEventListener('bcontact:afterFormSubmitted', (evt) => {
  if (evt.detail) {
    const formInstance = document.querySelector('.bcontact-' + evt.detail).closest('m-contact-form');
    formInstance.updateSuccessMessage();
    formInstance.clearOtherFormsSharedValues();
  }
});
